import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { BRAND } from '../config';
import * as XLSX from 'xlsx';
import { BRAND_NAME } from '../brand';

const Participants = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [participations, setParticipations] = useState([]);
  const [pdvs, setPdvs] = useState([]);
  const [prizes, setPrizes] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedParticipation, setSelectedParticipation] = useState(null);
  const [whatsappMessage, setWhatsappMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Fonction pour formater le numéro de téléphone camerounais
  const formatPhoneNumber = (phone) => {
    if (!phone) return '-';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.startsWith('237') && cleaned.length === 12) {
      return `+${cleaned.substring(0, 3)} ${cleaned.substring(3, 6)} ${cleaned.substring(6, 9)} ${cleaned.substring(9, 12)}`;
    }
    return phone;
  };

  const [filters, setFilters] = useState({
    city_id: '',
    pdv_id: '',
    prize_won: '',
    date_from: ''
  });

  const loadParticipations = async () => {
    try {
      const params = { brand: BRAND };
      if (filters.city_id) params.city_id = parseInt(filters.city_id);
      if (filters.pdv_id) params.pdv_id = parseInt(filters.pdv_id);
      if (filters.date_from) params.date_from = filters.date_from;

      const response = await api.get('/admin/participations', { params });

      // Vérifier que la réponse est valide
      if (!response.data) {
        throw new Error('Réponse vide du serveur');
      }

      if (!Array.isArray(response.data)) {
        console.error('Response is not an array:', response.data);
        throw new Error('Format de réponse invalide');
      }

      setParticipations(response.data);
      setMessage({ type: '', text: '' });
    } catch (error) {
      console.error('Error loading participations:', error);

      // Message d'erreur détaillé
      let errorMsg = 'Erreur lors du chargement des participants';

      if (error.code === 'ERR_NETWORK') {
        errorMsg = 'Erreur réseau - Vérifiez votre connexion';
      } else if (error.code === 'ERR_BAD_RESPONSE') {
        errorMsg = 'Données corrompues - La réponse est trop longue ou invalide';
      } else if (error.response?.status === 401) {
        errorMsg = 'Session expirée - Veuillez vous reconnecter';
        localStorage.removeItem('access_token');
        localStorage.removeItem('user_info');
        setTimeout(() => window.location.href = '/login', 2000);
        return;
      } else if (error.response?.data?.detail) {
        errorMsg = error.response.data.detail;
      } else if (error.message) {
        errorMsg = error.message;
      }

      setMessage({ type: 'danger', text: errorMsg });
      setParticipations([]); // Vider la liste en cas d'erreur
    } finally {
      setLoading(false);
    }
  };

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      loadParticipations();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadPdvs = async () => {
    try {
      const response = await api.get('/admin/pdv', { params: { brand: BRAND } });
      setPdvs(response.data);
    } catch (error) {
      console.error('Error loading PDVs:', error);
    }
  };

  const loadCities = async () => {
    try {
      const response = await api.get('/admin/cities');
      setCities(response.data);
    } catch (error) {
      console.error('Error loading cities:', error);
    }
  };

  const loadPrizes = async () => {
    try {
      const response = await api.get('/admin/prizes', { params: { brand: BRAND } });
      setPrizes(response.data);
    } catch (error) {
      console.error('Error loading prizes:', error);
    }
  };

  useEffect(() => {
    if (user?.role !== 'admin' && user?.role !== 'super_admin') {
      navigate('/dashboard');
      return;
    }
    loadCities();
    loadPdvs();
    loadPrizes();
  }, [user, navigate]);

  // Reload PDVs when city changes
  useEffect(() => {
    if (filters.city_id) {
      loadPdvs();
    }
  }, [filters.city_id]);

  // Charger les participations quand les filtres changent (hors filtre lot, géré côté front)
  useEffect(() => {
    loadParticipations();
  }, [filters.city_id, filters.pdv_id, filters.date_from]);

  const filteredParticipations = useMemo(() => {
    if (!filters.prize_won) return participations;
    const prizeType = filters.prize_won;

    return participations.filter((p) => {
      if (p?.plays_history && Array.isArray(p.plays_history)) {
        if (p.plays_history.some((play) => play?.prize_type === prizeType)) {
          return true;
        }
      }
      return p?.prize_won === prizeType;
    });
  }, [participations, filters.prize_won]);

  const handleSendWhatsApp = async () => {
    if (!selectedParticipation || !whatsappMessage.trim()) {
      setMessage({ type: 'danger', text: 'Veuillez entrer un message' });
      return;
    }

    try {
      const formData = new FormData();
      formData.append('message', whatsappMessage);

      const response = await api.post(`/admin/send-whatsapp/${selectedParticipation.id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      window.open(response.data.whatsapp_url, '_blank');

      setMessage({ type: 'success', text: 'Lien WhatsApp ouvert !' });
      setShowWhatsAppModal(false);
      setWhatsappMessage('');
      setSelectedParticipation(null);
    } catch (error) {
      setMessage({ type: 'danger', text: error.response?.data?.detail || "Erreur lors de l'envoi" });
    }
  };

  const handleViewDetails = async (participation) => {
    try {
      const response = await api.get(`/admin/participations/${participation.id}`);
      setSelectedParticipation(response.data);
      setShowDetailsModal(true);
    } catch (error) {
      setMessage({ type: 'danger', text: 'Erreur lors du chargement des détails' });
    }
  };

  const getPrizeLabel = (prizeType) => {
    if (!prizeType) return '-';
    const found = prizes.find(p => p.prize_type === prizeType);
    if (found) return found.prize_label || found.name || prizeType;
    if (prizeType.includes('reessayer') || prizeType.includes('try_again')) return 'Réessayez';
    return prizeType;
  };

  const exportToExcel = () => {
    // Préparer les données pour l'export
    const dataToExport = filteredParticipations.map(p => ({
      'Date': new Date(p.created_at).toLocaleDateString('fr-FR'),
      'Heure': new Date(p.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }),
      'Participant': p.participant_name,
      'Contact': p.participant_phone,
      'Ville': p.city_name || '-',
      'PDV': p.pdv_name,
      'Hôtesse': p.hotesse_name,
      'Mode': p.play_mode === 'whatsapp' ? 'WhatsApp' : p.play_mode === 'qr_code' ? 'QR Code' : 'Direct',
      'Statut': p.played ? 'Joué' : 'En attente',
      'Lot': p.plays_history && p.plays_history.length > 0 ? p.plays_history.map(play => `T${play.play_number}: ${getPrizeLabel(play.prize_type)}`).join(' • ') : (p.prize_won ? getPrizeLabel(p.prize_won) : '-'),
      'Code lot': p.prize_code || '-',
      'Livré': p.prize_delivered ? 'Oui' : 'Non',
      'Montant (FCFA)': p.bottles_count || 0,
      'Photo ticket': p.ticket_photo_path ? 'Oui' : 'Non'
    }));

    // Créer le workbook
    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Participants');

    // Générer le fichier
    XLSX.writeFile(workbook, `participants_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  if (loading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{ background: 'var(--page-bg)' }}>
        <div className="spinner-border text-danger" role="status"></div>
      </div>
    );
  }

  return (
    <div className="min-vh-100" style={{ background: 'var(--page-bg)' }}>
      {/* Header */}
      <nav className="navbar navbar-expand-lg navbar-light sticky-top" style={{ background: 'var(--surface)', padding: '12px 0' }}>
        <div className="container">
          <a className="navbar-brand fw-bold d-flex align-items-center" href="/dashboard" style={{ fontSize: '20px' }}>
            <div style={{ width: '40px', height: '40px', background: 'linear-gradient(135deg, var(--brand-primary) 0%, var(--brand-primary-dark) 100%)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '12px' }}>
              <i className="fas fa-bolt text-ink" style={{ fontSize: '18px' }}></i>
            </div>
            <span>{BRAND_NAME} Admin</span>
          </a>
          <div className="d-flex align-items-center">
            <span className="text-ink me-3">
              <i className="fas fa-user-shield me-2"></i>{user?.username}
            </span>
            <a href="/dashboard" className="btn btn-outline-light btn-sm">
              <i className="fas fa-arrow-left me-2"></i>Retour
            </a>
          </div>
        </div>
      </nav>

      <div className="container py-4">
        {message.text && (
          <div className={`alert alert-${message.type === 'success' ? 'success' : 'danger'} alert-dismissible fade show mb-3`} role="alert">
            {message.text}
            <button type="button" className="btn-close" onClick={() => setMessage({ type: '', text: '' })}></button>
          </div>
        )}

        {/* Page Header */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h2 className="text-ink fw-bold mb-1">
              <i className="fas fa-users me-2" style={{ color: 'var(--brand-primary)' }}></i>
              Gestion des Participants
            </h2>
            <p className="text-ink-muted mb-0">{filteredParticipations.length} participant(s)</p>
          </div>
          <button
            className="btn btn-success"
            onClick={exportToExcel}
            disabled={filteredParticipations.length === 0}
            style={{ background: '#28a745', color: '#ffffff', border: 'none', fontWeight: 'bold' }}
          >
            <i className="fas fa-file-excel me-2"></i>
            Exporter Excel
          </button>
        </div>

        {/* Filters */}
        <div className="card border-0 shadow mb-4" style={{ borderRadius: '12px', background: 'var(--surface)', border: '1px solid var(--surface-tint-red)' }}>
          <div className="card-body p-3">
            <div className="row g-3">
              <div className="col-md-2">
                <label className="form-label text-ink-muted small">Ville</label>
                <select
                  className="form-select form-select-sm"
                  value={filters.city_id}
                  onChange={(e) => setFilters({ ...filters, city_id: e.target.value, pdv_id: '' })}
                  style={{ background: 'var(--surface-tint)', borderColor: 'var(--surface-tint-red)', color: 'var(--text)' }}
                >
                  <option value="">Toutes les villes</option>
                  {cities.map(city => (
                    <option key={city.id} value={city.id}>{city.name}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-2">
                <label className="form-label text-ink-muted small">PDV</label>
                <select
                  className="form-select form-select-sm"
                  value={filters.pdv_id}
                  onChange={(e) => setFilters({ ...filters, pdv_id: e.target.value })}
                  disabled={!filters.city_id}
                  style={{ background: 'var(--surface-tint)', borderColor: 'var(--surface-tint-red)', color: 'var(--text)' }}
                >
                  <option value="">{filters.city_id ? 'Sélectionner un PDV' : 'Tous'}</option>
                  {pdvs.filter(p => !filters.city_id || p.city_id == filters.city_id).map(pdv => (
                    <option key={pdv.id} value={pdv.id}>{pdv.name}</option>
                  ))}
                </select>
              </div>
	              <div className="col-md-2">
	                <label className="form-label text-ink-muted small">Lot</label>
	                <select
	                  className="form-select form-select-sm"
	                  value={filters.prize_won}
	                  onChange={(e) => setFilters({ ...filters, prize_won: e.target.value })}
	                  style={{ background: 'var(--surface-tint)', borderColor: 'var(--surface-tint-red)', color: 'var(--text)' }}
	                >
	                  <option value="">Tous les lots</option>
	                  {prizes.map((prize) => (
	                    <option key={prize.id} value={prize.prize_type}>{prize.prize_label}</option>
	                  ))}
	                </select>
	              </div>
              <div className="col-md-2">
                <label className="form-label text-ink-muted small">Date début</label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={filters.date_from}
                  onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
                  style={{ background: 'var(--surface-tint)', borderColor: 'var(--surface-tint-red)', color: 'var(--text)' }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Participants Table */}
        <div className="card border-0 shadow" style={{ borderRadius: '12px', background: 'var(--surface)', border: '1px solid var(--surface-tint-red)' }}>
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead style={{ background: 'var(--text-muted)' }}>
                  <tr>
                    <th className="p-3" style={{ color: '#000000', fontWeight: 'bold', textTransform: 'uppercase', fontSize: '12px', letterSpacing: '0.5px' }}>Date/Heure</th>
                    <th className="p-3" style={{ color: '#000000', fontWeight: 'bold', textTransform: 'uppercase', fontSize: '12px', letterSpacing: '0.5px' }}>Participant</th>
                    <th className="p-3" style={{ color: '#000000', fontWeight: 'bold', textTransform: 'uppercase', fontSize: '12px', letterSpacing: '0.5px' }}>Contact</th>
                    <th className="p-3" style={{ color: '#000000', fontWeight: 'bold', textTransform: 'uppercase', fontSize: '12px', letterSpacing: '0.5px' }}>PDV</th>
                    <th className="p-3" style={{ color: '#000000', fontWeight: 'bold', textTransform: 'uppercase', fontSize: '12px', letterSpacing: '0.5px' }}>Hôtesse</th>
                    <th className="p-3" style={{ color: '#000000', fontWeight: 'bold', textTransform: 'uppercase', fontSize: '12px', letterSpacing: '0.5px' }}>Mode</th>
                    <th className="p-3" style={{ color: '#000000', fontWeight: 'bold', textTransform: 'uppercase', fontSize: '12px', letterSpacing: '0.5px' }}>Statut</th>
                    <th className="p-3" style={{ color: '#000000', fontWeight: 'bold', textTransform: 'uppercase', fontSize: '12px', letterSpacing: '0.5px' }}>Lot</th>
                    <th className="p-3" style={{ color: '#000000', fontWeight: 'bold', textTransform: 'uppercase', fontSize: '12px', letterSpacing: '0.5px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredParticipations.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="text-center py-5">
                        <i className="fas fa-users-slash fa-3x mb-3" style={{ opacity: 0.3, color: 'var(--brand-primary)' }}></i>
                        <p className="text-ink-muted mb-0">Aucun participant trouvé</p>
                      </td>
                    </tr>
                  ) : filteredParticipations.map((p, idx) => (
                    <tr key={p.id} style={{ cursor: 'pointer', backgroundColor: idx % 2 === 0 ? 'var(--surface-tint)' : 'transparent' }} onClick={() => handleViewDetails(p)}>
                      <td className="p-3">
                        <div style={{ fontSize: '12px', color: '#000000', fontWeight: 600, marginBottom: '2px' }}>{new Date(p.created_at).toLocaleDateString('fr-FR')}</div>
                        <div style={{ fontSize: '13px', color: '#000000', fontWeight: 600 }}>{new Date(p.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</div>
                      </td>
                      <td className="p-3 fw-semibold">{p.participant_name}</td>
                      <td className="p-3 font-monospace">{formatPhoneNumber(p.participant_phone)}</td>
                      <td className="p-3">{p.pdv_name}</td>
                      <td className="p-3">{p.hotesse_name}</td>
                      <td className="p-3">
                        <span className={`badge ${p.play_mode === 'whatsapp' ? 'bg-success' : p.play_mode === 'qr_code' ? 'bg-warning' : 'bg-primary'}`}>
                          <i className={`fas fa-${p.play_mode === 'whatsapp' ? 'fa-whatsapp' : p.play_mode === 'qr_code' ? 'qrcode' : 'globe'} me-1`}></i>
                          {p.play_mode === 'whatsapp' ? 'WhatsApp' : p.play_mode === 'qr_code' ? 'QR Code' : 'Direct'}
                        </span>
                      </td>
                      <td className="p-3">
                        {p.played ? (
                          <span className="badge bg-success">
                            <i className="fas fa-check-circle me-1"></i>Joué
                          </span>
                        ) : (
                          <span className="badge bg-secondary">
                            <i className="fas fa-clock me-1"></i>En attente
                          </span>
                        )}
                      </td>
                      <td className="p-3">
                        {p.plays_history && p.plays_history.length > 0 ? (
                          <div className="d-flex flex-column gap-1">
                            {p.plays_history.map((play, idx) => (
                              <span key={idx} className="badge bg-info" style={{ fontSize: '11px' }}>
                                Tour {play.play_number}: {getPrizeLabel(play.prize_type)}
                              </span>
                            ))}
                          </div>
                        ) : p.prize_won ? (
                          <span className="badge bg-info">{getPrizeLabel(p.prize_won)}</span>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td className="p-3" onClick={(e) => e.stopPropagation()}>
                        <button
                          className="btn btn-sm btn-success me-1"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedParticipation(p);
                            setShowWhatsAppModal(true);
                          }}
                          title="Envoyer WhatsApp"
                        >
                          <i className="fab fa-whatsapp"></i>
                        </button>
                        <button
                          className="btn btn-sm btn-info"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewDetails(p);
                          }}
                          title="Voir détails"
                        >
                          <i className="fas fa-eye"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* WhatsApp Modal */}
      {showWhatsAppModal && selectedParticipation && (
        <div className="modal show d-block" tabIndex="-1" style={{ background: 'rgba(30, 43, 36, 0.55)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{ background: 'var(--surface)', border: '2px solid var(--brand-deep)', color: 'var(--text)' }}>
              <div className="modal-header border-bottom-0">
                <h5 className="modal-title text-ink">
                  <i className="fab fa-whatsapp text-success me-2"></i>
                  Envoyer message WhatsApp
                </h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowWhatsAppModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label text-ink-muted">Participant</label>
                  <input
                    type="text"
                    className="form-control"
                    value={selectedParticipation.participant_name}
                    disabled
                    style={{ background: 'var(--surface-tint)', borderColor: 'var(--surface-tint-red)', color: 'var(--text)' }}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label text-ink-muted">Téléphone</label>
                  <input
                    type="text"
                    className="form-control"
                    value={selectedParticipation.participant_phone}
                    disabled
                    style={{ background: 'var(--surface-tint)', borderColor: 'var(--surface-tint-red)', color: 'var(--text)' }}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label text-ink-muted">Message</label>
                  <textarea
                    className="form-control"
                    rows="4"
                    value={whatsappMessage}
                    onChange={(e) => setWhatsappMessage(e.target.value)}
                    placeholder="Entrez votre message..."
                    style={{ background: 'var(--surface-tint)', borderColor: 'var(--surface-tint-red)', color: 'var(--text)' }}
                  ></textarea>
                </div>
              </div>
              <div className="modal-footer border-top-0">
                <button type="button" className="btn btn-secondary" onClick={() => setShowWhatsAppModal(false)}>Annuler</button>
                <button type="button" className="btn btn-success" onClick={handleSendWhatsApp}>
                  <i className="fab fa-whatsapp me-2"></i>Envoyer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedParticipation && (
        <div className="modal show d-block" tabIndex="-1" style={{ background: 'rgba(30, 43, 36, 0.55)' }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content" style={{ background: 'var(--surface)', border: '2px solid var(--brand-deep)', color: 'var(--text)' }}>
              <div className="modal-header border-bottom-0">
                <h5 className="modal-title text-ink">
                  <i className="fas fa-user me-2" style={{ color: 'var(--brand-primary)' }}></i>
                  Détails du participant
                </h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowDetailsModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label text-ink-muted small">Nom</label>
                    <div className="text-ink fw-semibold">{selectedParticipation.participant?.name}</div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label text-ink-muted small">Téléphone</label>
                    <div className="text-ink font-monospace">{formatPhoneNumber(selectedParticipation.participant?.phone)}</div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label text-ink-muted small">PDV</label>
                    <div className="text-ink">{selectedParticipation.pdv?.name}</div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label text-ink-muted small">Hôtesse</label>
                    <div className="text-ink">{selectedParticipation.hotesse?.name}</div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label text-ink-muted small">Session</label>
                    <div className="text-ink">{selectedParticipation.session?.name}</div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label text-ink-muted small">Mode de jeu</label>
                    <div className="text-ink">{selectedParticipation.play_mode === 'whatsapp' ? 'WhatsApp' : selectedParticipation.play_mode === 'qr_code' ? 'QR Code' : 'Direct'}</div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label text-ink-muted small">Date d'enregistrement</label>
                    <div className="text-ink">{new Date(selectedParticipation.created_at).toLocaleString('fr-FR')}</div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label text-ink-muted small">Montant du ticket</label>
                    <div className="text-ink">{Number(selectedParticipation.bottles_count || 0).toLocaleString('fr-FR')} FCFA</div>
                  </div>
                  {selectedParticipation.played && (
                    <>
                      <div className="col-md-6 mb-3">
                        <label className="form-label text-ink-muted small">Date du jeu</label>
                        <div className="text-ink">{selectedParticipation.played_at ? new Date(selectedParticipation.played_at).toLocaleString('fr-FR') : '-'}</div>
                      </div>
                      <div className="col-12 mb-3">
                        <label className="form-label text-ink-muted small">Lots par tour</label>
                        {selectedParticipation.plays_history && selectedParticipation.plays_history.length > 0 ? (
                          <div className="d-flex flex-wrap gap-2">
                            {selectedParticipation.plays_history.map((play, idx) => {
                              const isLosing = !play.prize_type || play.prize_type.includes('reessayer') || play.prize_type.includes('try_again');
                              return (
                                <div key={idx} className="p-2 rounded" style={{ background: 'var(--surface-tint)', border: '1px solid var(--surface-tint)' }}>
                                  <div className="text-ink-muted small">Tour {play.play_number}</div>
                                  <div className="fw-semibold" style={{ color: isLosing ? '#aaa' : '#6fcf6f' }}>
                                    {getPrizeLabel(play.prize_type)}
                                  </div>
                                  {play.prize_code && (
                                    <div className="font-monospace" style={{ fontSize: '11px', color: 'var(--brand-primary)' }}>{play.prize_code}</div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="text-ink">{selectedParticipation.prize_label || '-'}</div>
                        )}
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label text-ink-muted small">Statut livraison</label>
                        <div className="text-ink">
                          {selectedParticipation.prize_delivered ? (
                            <span className="badge bg-success">Livré</span>
                          ) : (
                            <span className="badge bg-secondary">Non livré</span>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                  {selectedParticipation.ticket_photo_url && (
                    <div className="col-12 mb-3">
                      <label className="form-label text-warning fw-bold">
                        <i className="fas fa-receipt me-2"></i>Photo du ticket de caisse
                      </label>
                      <div className="p-3 rounded" style={{ background: 'var(--surface-tint-red)', border: '1px solid var(--surface-tint-red)' }}>
                        <a href={selectedParticipation.ticket_photo_url} target="_blank" rel="noopener noreferrer">
                          <img
                            src={selectedParticipation.ticket_photo_url}
                            alt="Ticket de caisse"
                            style={{ maxWidth: '100%', maxHeight: '400px', borderRadius: '8px', cursor: 'pointer' }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'block';
                            }}
                          />
                          <div style={{ display: 'none' }} className="text-center text-warning mt-2">
                            <i className="fas fa-exclamation-triangle me-2"></i>
                            Image non disponible
                          </div>
                        </a>
                        <div className="text-center mt-2">
                          <small className="text-ink-muted">Cliquez sur l'image pour l'agrandir</small>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer border-top-0">
                <button type="button" className="btn btn-primary" onClick={() => {
                  setShowDetailsModal(false);
                  setSelectedParticipation(p => ({ ...p, participant: selectedParticipation.participant }));
                  setShowWhatsAppModal(true);
                }}>
                  <i className="fab fa-whatsapp me-2"></i>Envoyer WhatsApp
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowDetailsModal(false)}>Fermer</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Participants;
